import { IAccount, IAccountTeam } from '@ViewModels';
import { css } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { toast } from 'sonner';
import * as AdminViewModels from '../../../../app/viewmodels/AdminViewModels';
import { IModalContext, ModalChildComponentContextKey } from '../../../models';
import { Button } from '../../../web/components/Button';
import { LoadingSpinner } from '../../../web/components/LoadingSpinner';
import { asModalComponent } from '../../../web/components/Modal';
import { RadioButton } from '../../../web/components/RadioButton';
import { ClipboardIcon } from '../../../web/components/svgs/icons/ClipboardIcon';
import { ScheduleMeetingLargeCalendar } from '../../../web/components/svgs/icons/ScheduleMeetingLargeCalendar';
import { styleSheet } from './styles';

interface IProps extends IModalContext {
	account?: IAccount;
	className?: string;
	userSession: AdminViewModels.AdminUserSessionContext;
}

const _ScheduleMeetingModal = ({ account, userSession, parentModal: { onRequestClose } }: IProps) => {
	const mAccountOperations: AdminViewModels.AccountOperationsViewModel = React.useMemo(() => {
		return new AdminViewModels.AccountOperationsViewModel(userSession, { id: account.id });
	}, [account, userSession]);
	const [teams, setTeams] = React.useState<IAccountTeam[]>([]);

	const [selectedOption, setSelectedOption] = React.useState<number>(null);
	const [showMainLoader, setShowMainLoader] = React.useState<boolean>(true);
	const [showUrlLoader, setShowUrlLoader] = React.useState<boolean>(false);
	const [showCopyLinkLoader, setShowCopyLinkLoader] = React.useState<boolean>(false);

	const [meetingUrl, setMeetingUrl] = React.useState<string>(null);

	const [shortcutUrl, setShortcutUrl] = React.useState<string>(null);

	React.useEffect(() => {
		mAccountOperations
			.getTeamsByAccountId()
			.then(result => {
				setTeams(result);
				setShowMainLoader(false);
			})
			.catch(err => {
				setShowMainLoader(false);
				throw err;
			});

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	React.useEffect(() => {
		if (selectedOption === 0 || selectedOption > 0) {
			setShowUrlLoader(true);
			setShowCopyLinkLoader(true);
			mAccountOperations

				.getScheduleMeetingUrl(account, teams[selectedOption].name)
				.then(result => {
					setMeetingUrl(result.url);
					setShowUrlLoader(false);
				})
				.catch(err => {
					setMeetingUrl(null);
					setShowUrlLoader(false);
					throw err;
				});
			mAccountOperations

				.getScheduleMeetingShortcut(account, teams[selectedOption].name)
				.then(res => {
					setShortcutUrl(res.url);
					setShowCopyLinkLoader(false);
				})
				.catch(err => {
					setShortcutUrl(null);
					setShowCopyLinkLoader(false);
					throw err;
				});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedOption]);

	const handleContinueClick = () => {
		if (!meetingUrl) {
			return;
		}
		window.open(meetingUrl, '_blank');
		onRequestClose();
	};
	const handleCopyLInkClick = () => {
		if (!shortcutUrl) {
			return;
		}

		navigator.clipboard.writeText(shortcutUrl).then(() => {
			toast.success('Meeting was successfully copied to clipboard');
		});
		onRequestClose();
	};
	const onChange = (idx: number) => {
		setSelectedOption(idx);
	};

	const LoaderIndicator = () => {
		return showMainLoader ? <LoadingSpinner type='small' /> : null;
	};

	const handleCancel = () => {
		onRequestClose();
	};

	return (
		<div className={css(styleSheet.wrapper)}>
			<figure>
				<ScheduleMeetingLargeCalendar />
			</figure>
			<h3 className={css(styleSheet.heading)}>Schedule Meeting</h3>
			<p className={css(styleSheet.text)}>What type of meeting would you like to schedule?</p>
			<div className={css(styleSheet.radioButtonsWrap)}>
				<LoaderIndicator />
				{teams.map((team: IAccountTeam, idx: number) => {
					return (
						<div key={team.name} className={css(styleSheet.half)}>
							<RadioButton
								key={team.name}
								checked={idx === selectedOption}
								id={`radio-${team.name}`}
								name={team.name}
								onChange={() => onChange(idx)}
							>
								<div>{team.label}</div>
							</RadioButton>
						</div>
					);
				})}
			</div>
			<footer className={css(styleSheet.footer)}>
				<Button
					kind='primary'
					className={css(styleSheet.continueButton)}
					label='Continue'
					onClick={handleContinueClick}
					isLoading={showUrlLoader}
					disabled={!meetingUrl && !showUrlLoader}
				/>
				<Button
					accessory={<ClipboardIcon fillColor='#046CB6' />}
					className={css(styleSheet.copyLinkButton)}
					kind='icon'
					label='Copy Link'
					isLoading={showCopyLinkLoader}
					onClick={handleCopyLInkClick}
					disabled={!shortcutUrl && !showCopyLinkLoader}
				/>
				<Button kind='reverse' label='Cancel' onClick={handleCancel} />
			</footer>
		</div>
	);
};

const ScheduleMeetingModalAsObserver = observer(_ScheduleMeetingModal);
const ScheduleMeetingModalWithContext = inject(ModalChildComponentContextKey)(ScheduleMeetingModalAsObserver);
export const ScheduleMeetingModal = asModalComponent(ScheduleMeetingModalWithContext, {
	className: 'schedule-meeting--modal',
	useDefaultHeader: true,
});
